<!-- src/apps/secret/components/branded/SecretDisplayCase.vue -->

<script setup lang="ts">
  import { useI18n } from 'vue-i18n';
  import BaseSecretDisplay from '@/apps/secret/components/branded/BaseSecretDisplay.vue';
  import { useClipboard } from '@/shared/composables/useClipboard';
  import type { Secret, SecretDetails } from '@/schemas/shapes/v3/secret';
  import { brandSettingsSchema } from '@/schemas/shapes/v3/custom-domain';
  import { useProductIdentity } from '@/shared/stores/identityStore';
  import { ref, computed } from 'vue';

  // Default brand settings for when no custom branding is configured
  const defaultBrandSettings = brandSettingsSchema.parse({});

  interface Props {
    record: Secret | null;
    details: SecretDetails | null;
    domainId: string;
    submissionStatus?: {
      status: 'idle' | 'submitting' | 'success' | 'error';
      message?: string;
    };
  }

  const props = defineProps<Props>();
  const i18n = useI18n();
  const { t } = i18n;

  const isRevealed = computed(() => !!props.record?.secret_value && props.record.secret_value !== '');

  const productIdentity = useProductIdentity();

  // Use computed properties directly from identityStore (already parsed with v3 schema)
  const cornerClass = computed(() => productIdentity.cornerClass);
  const fontFamilyClass = computed(() => productIdentity.fontFamilyClass);

  const alertClasses = computed(() => ({
    'mb-4 p-4 rounded-md': true,
    'bg-branddim-50 text-branddim-700 dark:bg-branddim-900 dark:text-branddim-100':
      props.submissionStatus?.status === 'error',
    'bg-brand-50 text-brand-700 dark:bg-brand-900 dark:text-brand-100':
      props.submissionStatus?.status === 'success',
  }));

  const hasImageError = ref(false);
  const { isCopied, copyToClipboard } = useClipboard();

  const logoAriaLabel = hasImageError.value ? t('web.branding.default_logo_icon') : t('web.layout.brand_logo')
  const copySecretContent = async () => {
    if (props.record?.secret_value === undefined) {
      return;
    }

    await copyToClipboard(props.record?.secret_value);

    // Announce copy success to screen readers
    const announcement = document.createElement('div');
    announcement.setAttribute('role', 'status');
    announcement.setAttribute('aria-live', 'polite');
    announcement.textContent = t('web.secrets.secret_content_copied_to_clipboard');
    document.body.appendChild(announcement);
    setTimeout(() => announcement.remove(), 1000);
  };

  const handleImageError = () => {
    hasImageError.value = true;
  };
  const isCopiedText = computed(() => isCopied ? t('web.STATUS.copied') : t('web.LABELS.copy_to_clipboard') );

  // Prepare the standardized path to the logo image.
  // Note that the file extension needs to be present but is otherwise not used.
  const logoImage = ref<string>(`/imagine/${props.domainId}/logo.png`);
</script>

<template>
  <!-- Updated -->
  <BaseSecretDisplay
    :default-title="t('web.secrets.you_have_a_message')"
    :preview-i18n="i18n"
    :domain-branding="productIdentity.brand ?? defaultBrandSettings"
    :corner-class="cornerClass"
    :font-class="fontFamilyClass"
    :is-revealed="isRevealed">
    <!-- Alert display -->
    <div
      v-if="
        submissionStatus?.status === 'error' || submissionStatus?.status === 'success'
      "
      :class="alertClasses"
      role="alert"
      aria-live="polite">
      <div class="flex">
        <div class="shrink-0">
          <svg
            v-if="submissionStatus.status === 'error'"
            class="size-5"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
              clip-rule="evenodd" />
          </svg>
          <svg
            v-else
            class="size-5"
            viewBox="0 0 20 20"
            fill="currentColor"
            aria-hidden="true">
            <path
              fill-rule="evenodd"
              d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
              clip-rule="evenodd" />
          </svg>
        </div>
        <div class="ml-3">
          <p class="text-sm">
            {{
              submissionStatus.message ||
                (submissionStatus.status === 'error' ? t('web.COMMON.an_error_occurred') : t('web.STATUS.success'))
            }}
          </p>
        </div>
      </div>
    </div>

    <template #logo>
      <!-- Brand Icon -->
      <div class="relative mx-auto sm:mx-0">
        <router-link to="/">
          <div
            :class="[cornerClass]"
            class="flex size-14 items-center justify-center bg-gray-100 dark:bg-gray-700 sm:size-16"
            role="img"
            :aria-label="logoAriaLabel">
            <!-- Default lock icon -->
            <svg
              v-if="!logoImage || hasImageError"
              class="size-8 text-gray-400 dark:text-gray-500"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              aria-hidden="true">
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
            </svg>

            <!-- Logo -->
            <img
              v-if="logoImage && !hasImageError"
              :src="logoImage"
              :alt="t('web.layout.brand_logo')"
              class="size-16 object-contain"
              :class="[cornerClass]"
              @error="handleImageError" />
          </div>
        </router-link>
      </div>
    </template>

    <template #content>
      <div class="relative size-full p-0">
        <div :class="[cornerClass, 'size-full overflow-hidden border border-gray-300 bg-gray-100 dark:border-gray-600 dark:bg-gray-800']">
          <label
            :for="'secret-content-' + record?.identifier"
            class="sr-only">
            {{ t('web.secrets.secret_content') }}
          </label>
          <textarea
            :id="'secret-content-' + record?.identifier"
            class="block size-full min-h-32 resize-none border-none bg-transparent font-mono text-base focus:outline-none focus:ring-2 focus:ring-brand-500 dark:text-white sm:min-h-36"
            readonly
            :rows="details?.display_lines ?? 4"
            :value="record?.secret_value"
            :aria-label="t('web.secrets.secret_content')"
            data-testid="secret-content"
            ref="secretContent"></textarea>
        </div>
      </div>
    </template>

    <template #action-button>
      <button
        @click="copySecretContent"
        :title="isCopiedText"
        class="inline-flex items-center justify-center rounded-md px-4 py-2.5 text-sm font-medium text-brand-700 shadow-sm transition-colors duration-150 ease-in-out hover:shadow focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:text-brand-100"
        :class="[fontFamilyClass, cornerClass]"
        :style="{
          backgroundColor: productIdentity.primaryColor,
          color: productIdentity.buttonTextLight ? '#ffffff' : '#000000'
        }"
        aria-live="polite"
        :aria-label="isCopied ? t('web.COMMON.secret_copied_to_clipboard') : t('web.COMMON.copy_secret_to_clipboard')"
        :aria-pressed="isCopied">
        <svg
          v-if="!isCopied"
          xmlns="http://www.w3.org/2000/svg"
          class="mr-2 size-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
        </svg>
        <svg
          v-else
          xmlns="http://www.w3.org/2000/svg"
          class="mr-2 size-5"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          aria-hidden="true">
          <path
            stroke-linecap="round"
            stroke-linejoin="round"
            stroke-width="2"
            d="M5 13l4 4L19 7" />
        </svg>
        <span>{{ isCopied ? t('web.STATUS.copied') : t('web.LABELS.copy_to_clipboard') }}</span>
      </button>
    </template>
  </BaseSecretDisplay>
</template>

<style scoped>
  /* Ensure focus outline is visible in all color schemes */
  :focus {
    outline: 2px solid currentColor;
    outline-offset: 2px;
  }
</style>
